import { useEffect, useRef, useState } from 'react';
// The workbench's gallery page, served LIVE through the preview pool — the
// studio server spawns a Vite child for the workbench dir on first request,
// so the tiles always render the template files currently on disk (the same
// files the server's template scan reads). Never a static build to go stale.
//
// This lives in lib/ rather than beside the chooser that renders it because
// `useDeckEditor` warms the gallery on mount, and a hook reaching into a
// component module for a bare `fetch` is the layering the wrong way round —
// which `allowJs: false` turned from untidy into unbuildable, since a `.ts`
// module cannot import a `.jsx` one at all.
export const GALLERY_URL = '/preview/__workbench/gallery.html';

/** The gallery's entry module. Fetching the HTML only proves Vite's server is
 *  up; fetching this makes it TRANSFORM the graph, which is the slow part. */
const GALLERY_ENTRY_URL = '/preview/__workbench/src/gallery/main.jsx';

/** Fire-and-forget warm-up: the first gallery request spawns the workbench
 *  Vite child server-side (seconds of cold start). Calling this when the
 *  editor mounts means the child is already running by the time anyone
 *  clicks +, so the modal's skeleton barely flashes.
 *
 *  It asks for the ENTRY MODULE as well as the page. Fetching only the HTML
 *  spawned the child but left its module graph cold, so the expensive work —
 *  transforming an eager glob of every template, each importing the whole
 *  primitives module — still happened after the modal opened. That is what
 *  made the first "+ Slide" a grey panel and the second one instant. */
export function warmGallery(): void {
  fetch(GALLERY_URL)
    .then(() => fetch(GALLERY_ENTRY_URL))
    .catch(() => {});
}

/** The probe is bounded: the workbench child may be cold, and an unbounded
 *  one leaves the modal on its skeleton with nothing able to end the wait. */
const GALLERY_PROBE_TIMEOUT_MS = 20_000;

/** How long the iframe gets to fire `load` before we give up on it and fall
 *  back to the text list. */
const GALLERY_LOAD_TIMEOUT_MS = 45_000;

type GalleryState = 'loading' | 'ready' | 'unavailable';

/**
 * The gallery's three-state loader, shared by the two choosers that mount it.
 *
 * `AddSlideChooser` and `ChangeTemplateChooser` ran this line for line: the
 * same body-checking probe, the same paint timeout, the same `/api/templates`
 * fallback. The copy had already started to drift — the second one inlined
 * `45_000` where the first names it, had no probe timeout at all, and
 * re-declared `GALLERY_URL` locally even though the first imports it from
 * here and the comment above it says the constant moved.
 *
 * The states:
 *   loading      probing (and warming) the workbench child
 *   ready        the server answered with the gallery HTML — NOT that the
 *                frame has painted; the module graph compiles after that
 *   unavailable  show the text list instead
 *
 * `ready` must stay falsifiable. It used to be terminal, so a gallery that
 * answered its HTML and then never finished booting left the modal on a grey
 * panel indefinitely.
 *
 * @returns `onPainted` for the iframe's `load` (until then the frame is an
 *          empty grey panel and should stay hidden) and `onFailed` for its
 *          `error`, which drops straight to the text list.
 */
export function useTemplateGallery<T>(fetchTemplates: () => Promise<T[]>) {
  const [state, setState] = useState<GalleryState>('loading');
  const [painted, setPainted] = useState(false);
  const [templates, setTemplates] = useState<T[] | null>(null);

  // Probe (and warm) the live gallery. Body check, not just status: the
  // child's SPA fallback answers 200 with index.html even when gallery.html is
  // missing, and that page must not be iframed.
  useEffect(() => {
    let cancelled = false;
    fetch(GALLERY_URL, { signal: AbortSignal.timeout(GALLERY_PROBE_TIMEOUT_MS) })
      .then(async (res) => {
        // Extension-agnostic: the entry is main.tsx since the TypeScript
        // overhaul, and hardcoding either extension silently degrades this to
        // the text-list fallback rather than failing anywhere visible.
        const ok = res.ok && /src\/gallery\/main\.[jt]sx/.test(await res.text());
        if (!cancelled) setState(ok ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!cancelled) setState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state !== 'ready' || painted) return;
    const t = setTimeout(() => setState('unavailable'), GALLERY_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [state, painted]);

  const load = useRef(fetchTemplates);
  load.current = fetchTemplates;
  useEffect(() => {
    if (state !== 'unavailable') return;
    let cancelled = false;
    load
      .current()
      .then((list) => {
        if (!cancelled) setTemplates(list);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  return {
    state,
    painted,
    templates,
    onPainted: () => setPainted(true),
    onFailed: () => setState('unavailable'),
  };
}
